// The Brugere tab slices the user list along several dimensions at once, and which
// dimensions are useful keeps changing. So the dimensions are data rather than markup: the
// tab renders one checkbox group per entry in USER_FILTER_DIMENSIONS and asks `matches`
// whether a user belongs. Adding a new way to slice the list means adding an entry here —
// the toolbar, the filter state and the table stay untouched.
//
// A dimension is { key, label, buildOptions(context), matches(user, value) }, where
// `context` carries the loaded receivers and companies so a dimension can enumerate real
// ids instead of hard-coding them.

const hasAnyAccess = (user) =>
    (user?.companies?.length || 0) > 0 || (user?.wasteReceivers?.length || 0) > 0;

export const USER_FILTER_DIMENSIONS = [
    {
        key: 'wasteReceiver',
        label: 'Affaldsmodtager',
        buildOptions: ({ wasteReceivers = [] } = {}) =>
            wasteReceivers.map((receiver) => ({
                value: receiver.id,
                label: receiver.name || receiver.label || receiver.id,
            })),
        matches: (user, value) => (user?.wasteReceivers || []).includes(value),
    },
    {
        key: 'company',
        label: 'Virksomhed',
        buildOptions: ({ companies = [] } = {}) =>
            companies.map((company) => ({
                value: company.id,
                label: company.label || company.name || company.id,
            })),
        matches: (user, value) => (user?.companies || []).includes(value),
    },
    {
        key: 'role',
        label: 'Rolle',
        buildOptions: () => [
            { value: 'admin', label: 'Admin' },
            { value: 'non_admin', label: 'Ikke admin' },
        ],
        matches: (user, value) =>
            value === 'admin' ? user?.isAdmin === true : user?.isAdmin !== true,
    },
    {
        key: 'access',
        label: 'Adgang',
        buildOptions: () => [
            { value: 'with_access', label: 'Med adgang' },
            { value: 'without_access', label: 'Uden adgang' },
        ],
        matches: (user, value) =>
            value === 'with_access' ? hasAnyAccess(user) : !hasAnyAccess(user),
    },
];

// A dimension with no options left (no receivers created yet, say) would render as an empty
// checkbox group, so it is dropped here rather than in the markup.
export function buildUserFilterGroups(context = {}, dimensions = USER_FILTER_DIMENSIONS) {
    return dimensions
        .map((dimension) => ({
            key: dimension.key,
            label: dimension.label,
            options: dimension.buildOptions(context) || [],
        }))
        .filter((group) => group.options.length > 0);
}

// `selections` is { [dimensionKey]: [selectedValue, ...] }. Within one dimension the
// selected values are OR'ed — ticking two receivers means "either receiver" — and the
// dimensions are AND'ed together. A dimension with nothing ticked filters nothing, which is
// what makes an untouched filter panel show every user.
export function filterUsers(users = [], selections = {}, dimensions = USER_FILTER_DIMENSIONS) {
    const activeDimensions = dimensions.filter(
        (dimension) => (selections?.[dimension.key]?.length || 0) > 0,
    );

    if (activeDimensions.length === 0) return [...users];

    return users.filter((user) =>
        activeDimensions.every((dimension) =>
            selections[dimension.key].some((value) => dimension.matches(user, value)),
        ),
    );
}

export function countActiveUserFilters(selections = {}) {
    return Object.values(selections).reduce(
        (total, values) => total + (Array.isArray(values) ? values.length : 0),
        0,
    );
}

// Free-text search covers what an admin has at hand when looking someone up: a name, an
// email, a uid pasted from the Firebase console — and the receivers and companies the user
// is attached to, which is how you find "everyone on Marius Pedersen" without hunting for
// the right tick box. Attachments are stored as ids, so both the id and its display name
// are searched; without a label resolver only the ids match.
export function matchesUserSearch(user, query, labels = {}) {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return true;

    const { getWasteReceiverLabel, getCompanyLabel } = labels;
    const fields = [user?.label, user?.email, user?.id];

    (user?.wasteReceivers || []).forEach((receiverId) => {
        fields.push(receiverId);
        if (getWasteReceiverLabel) fields.push(getWasteReceiverLabel(receiverId));
    });
    (user?.companies || []).forEach((companyId) => {
        fields.push(companyId);
        if (getCompanyLabel) fields.push(getCompanyLabel(companyId));
    });

    return fields
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
}

export function searchUsers(users = [], query, labels = {}) {
    return users.filter((user) => matchesUserSearch(user, query, labels));
}

// localeCompare('da') is what puts æ, ø and å after z instead of next to a and o.
const DANISH_COLLATOR = new Intl.Collator('da', { sensitivity: 'base', numeric: true });

const compareText = (left, right) => DANISH_COLLATOR.compare(left || '', right || '');

const compareNumbers = (left, right) => left - right;

// An attachment column sorts on two things at once. How many attachments a user has comes
// first, so the people wired into several receivers — the ones worth looking at — sit at
// the top instead of hiding under whichever name happens to sort first. Then the attachment
// names, collated and joined, so that within one count the users of a receiver land in a
// contiguous block and the blocks run alphabetically. Ids stand in for the names when no
// label resolver is supplied.
const buildAttachmentSortValue = (ids = [], getLabel) => {
    const labels = (ids || []).map((id) => (getLabel ? getLabel(id) : id) || id);
    labels.sort(DANISH_COLLATOR.compare);
    return { count: labels.length, labels: labels.join(' | ') };
};

const compareAttachments = (left, right) =>
    right.count - left.count || compareText(left.labels, right.labels);

// Sortable columns are data for the same reason the filters are: the table header renders
// from this array, so a new sortable column is an entry here plus its cell in the page.
//
// `isMissing` marks the rows that carry no value for the column — a user with nothing but a
// uid, no email, or no attachments at all. They sort to the bottom in both directions
// rather than flipping to the top when the sort is reversed, because they are noise
// wherever they land alphabetically.
export const USER_SORT_COLUMNS = [
    {
        key: 'name',
        label: 'Navn',
        // normalizeUserDoc falls back name -> display_name -> email -> user_id -> document
        // id, so a user with nothing at all ends up with `label === id`.
        isMissing: (user) => !user?.label || user.label === user?.id,
        getSortValue: (user) => user?.label || '',
    },
    {
        key: 'wasteReceivers',
        label: 'Affaldsmodtagere',
        isMissing: (user) => (user?.wasteReceivers?.length || 0) === 0,
        getSortValue: (user, labels = {}) =>
            buildAttachmentSortValue(user?.wasteReceivers, labels.getWasteReceiverLabel),
        compareValues: compareAttachments,
    },
    {
        key: 'companies',
        label: 'Virksomheder',
        isMissing: (user) => (user?.companies?.length || 0) === 0,
        getSortValue: (user, labels = {}) =>
            buildAttachmentSortValue(user?.companies, labels.getCompanyLabel),
        compareValues: compareAttachments,
    },
    {
        key: 'admin',
        label: 'Admin',
        getSortValue: (user) => (user?.isAdmin === true ? 0 : 1),
        compareValues: compareNumbers,
    },
];

export const DEFAULT_USER_SORT_COLUMN = USER_SORT_COLUMNS[0].key;

export function sortUsers(
    users = [],
    columnKey = DEFAULT_USER_SORT_COLUMN,
    direction = 'asc',
    labels = {},
) {
    const column =
        USER_SORT_COLUMNS.find((entry) => entry.key === columnKey) || USER_SORT_COLUMNS[0];
    const factor = direction === 'desc' ? -1 : 1;
    const compareValues = column.compareValues || compareText;

    // Decorated once rather than per comparison: a sort value can resolve several ids to
    // their labels, which is far too much work to redo on every visit in the sort.
    const decorated = users.map((user) => ({
        user,
        missing: column.isMissing ? Boolean(column.isMissing(user)) : false,
        value: column.getSortValue(user, labels),
    }));

    decorated.sort((a, b) => {
        if (a.missing !== b.missing) return a.missing ? 1 : -1;

        return (
            factor * compareValues(a.value, b.value) ||
            // Rows the column itself cannot tell apart — the users of one receiver, or
            // everyone who is not an admin — read as an arbitrary jumble unless they are
            // ordered by something. Name, then id, and neither is reversed, so flipping the
            // sort reorders the blocks without shuffling the rows inside them.
            compareText(a.user.label, b.user.label) ||
            DANISH_COLLATOR.compare(a.user.id, b.user.id)
        );
    });

    return decorated.map((entry) => entry.user);
}

// The one call the tab makes: filter, search, then sort.
export function selectVisibleUsers(
    users = [],
    {
        selections = {},
        search = '',
        labels = {},
        sortColumn = DEFAULT_USER_SORT_COLUMN,
        sortDirection = 'asc',
    } = {},
    dimensions = USER_FILTER_DIMENSIONS,
) {
    return sortUsers(
        searchUsers(filterUsers(users, selections, dimensions), search, labels),
        sortColumn,
        sortDirection,
        labels,
    );
}
